import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";

import store from "../../state/Store";
import Datastore from "../../communication/Datastore";
import PhoneNumber from "../../types/core/PhoneNumber";
import EmailAddress from "../../types/core/EmailAddress";
import Address from "../../types/core/Address";
import Contact from "../../types/core/Contact";

// Define the type for our state store entry
type ContactState = {
    status: string;
    titles: string[];
    newNumberStatus: string;
    newNumber: PhoneNumber;
    newEmailStatus: string;
    newEmail: EmailAddress;
    newAddressStatus: string;
    newAddress: Address;
    deleteNumberStatus: string;
    deleteEmailStatus: string;
    deleteAddressStatus: string;
    error: string;
};

// Define the initial state for our state store entry
const initialState: ContactState = {
    status: "idle",
    titles: [],
    newNumberStatus: "idle",
    newNumber: null,
    newEmailStatus: "idle",
    newEmail: null,
    newAddressStatus: "idle",
    newAddress: null,
    deleteNumberStatus: "idle",
    deleteEmailStatus: "idle",
    deleteAddressStatus: "idle",
    error: null,
};

// Create a slice to hold all our reducers
const contactSlice = createSlice({
    name: "contact",
    initialState: initialState,
    reducers: {},
    extraReducers(builder) {
        builder
            .addCase(REHYDRATE, (_, rawData: any) => {
                if (rawData?.key !== "tts") return undefined;
                return {
                    status:
                        !rawData?.payload?.contact?.titles ||
                        !rawData?.payload?.contact?.titles.length
                            ? "idle"
                            : rawData?.payload?.contact?.status,
                    titles: rawData?.payload?.contact?.titles ?? [],
                    newNumberStatus: !rawData?.payload?.contact?.newNumber
                        ? "idle"
                        : rawData?.payload?.contact?.newNumberStatus,
                    newNumber: rawData?.payload?.contact?.newNumber,
                    newEmailStatus: !rawData?.payload?.contact?.newEmail
                        ? "idle"
                        : rawData?.payload?.contact?.newEmailStatus,
                    newEmail: rawData?.payload?.contact?.newEmail,
                    newAddressStatus: !rawData?.payload?.contact?.newAddress
                        ? "idle"
                        : rawData?.payload?.contact?.newAddressStatus,
                    newAddress: rawData?.payload?.contact?.newAddress,
                    deleteNumberStatus: "idle",
                    deleteEmailStatus: "idle",
                    deleteAddressStatus: "idle",
                    error: null,
                };
            })
            .addCase(fetchTitles.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(fetchTitles.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                state.titles = action.payload[0];
            })
            .addCase(fetchTitles.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(fetchNewNumber.pending, (state, action) => {
                state.newNumberStatus = "loading";
            })
            .addCase(fetchNewNumber.fulfilled, (state, action) => {
                state.newNumberStatus = "succeeded";
                // Add any fetched posts to the array
                state.newNumber = action.payload;
            })
            .addCase(fetchNewNumber.rejected, (state, action) => {
                state.newNumberStatus = "failed";
                state.error = action.error.message;
            })
            .addCase(fetchNewEmail.pending, (state, action) => {
                state.newEmailStatus = "loading";
            })
            .addCase(fetchNewEmail.fulfilled, (state, action) => {
                state.newEmailStatus = "succeeded";
                // Add any fetched posts to the array
                state.newEmail = action.payload;
            })
            .addCase(fetchNewEmail.rejected, (state, action) => {
                state.newEmailStatus = "failed";
                state.error = action.error.message;
            })
            .addCase(fetchNewAddress.pending, (state, action) => {
                state.newAddressStatus = "loading";
            })
            .addCase(fetchNewAddress.fulfilled, (state, action) => {
                state.newAddressStatus = "succeeded";
                // Add any fetched posts to the array
                state.newAddress = action.payload;
            })
            .addCase(fetchNewAddress.rejected, (state, action) => {
                state.newAddressStatus = "failed";
                state.error = action.error.message;
            })
            .addCase(deleteNumber.rejected, (state, action) => {
                state.deleteNumberStatus = "failed";
                state.error = action.error.message;
            })
            .addCase(deleteEmail.rejected, (state, action) => {
                state.deleteEmailStatus = "failed";
                state.error = action.error.message;
            })
            .addCase(deleteAddress.rejected, (state, action) => {
                state.deleteAddressStatus = "failed";
                state.error = action.error.message;
            });
    },
});

// This is a thunk, they are used to load the state
const fetchTitles = createAsyncThunk("contact/fetchTitles", async () => {
    const response = await Promise.all([
        Datastore.getType<string[]>("api/Contacts/Titles"),
    ]);
    return response;
});

export const initialiseTitleDetails = createAsyncThunk(
    "contact/initialiseTitleDetails",
    async (_, thunkAPI) => {
        const { contact } = store.getState().db;

        if (contact.status === "idle") await thunkAPI.dispatch(fetchTitles());
    },
);

/******* Contacts Management *******/
export const getContacts = async (personId: number) => {
    return (
        await Datastore.getType<Contact[]>(`api/Contacts/Person/${personId}`)
    ).map(c => new Contact(c));
};
export const saveContacts = async (contacts: Contact[]) => {
    const waits = [];
    const adds = contacts.filter(c => !c.id);
    const updates = contacts.filter(c => c.id);

    if (adds.length)
        waits.push(Datastore.postType<Contact[]>(`api/Contacts`, adds));
    if (updates.length)
        waits.push(Datastore.putType<Contact[]>(`api/Contacts`, updates));

    const result = waits.length ? await Promise.all(waits) : [];
    return result.flat();
};

/******* Phone Number Management *******/
interface SaveNumberType {
    personId: number;
    number: PhoneNumber;
}
const fetchNewNumber = createAsyncThunk("contact/fetchNewNumber", async () => {
    const response = await Datastore.getType<PhoneNumber>(
        "api/PhoneNumbers/New",
    );
    return response;
});
export const saveNewNumber = createAsyncThunk(
    "contact/saveNewNumber",
    async (payload: SaveNumberType) => {
        const response = await Datastore.postType<PhoneNumber[]>(
            `api/PhoneNumbers/People/${payload.personId}`,
            [payload.number],
        );
        return response;
    },
);
export const saveNumber = createAsyncThunk(
    "contact/saveNumber",
    async (number: PhoneNumber) => {
        const response = await Datastore.putType<PhoneNumber[]>(
            "api/PhoneNumbers",
            [number],
        );
        return response;
    },
);
export const deleteNumber = createAsyncThunk(
    "contact/deleteNumber",
    async (number: PhoneNumber) => {
        const response = await Datastore.delete(
            `api/PhoneNumbers/${number.id}`,
        );
        return response;
    },
);
export const getNewNumber = createAsyncThunk(
    "contact/getNewNumber",
    async (_, thunkAPI) => {
        const { contact } = store.getState().db;

        if (contact.newNumberStatus === "idle")
            await thunkAPI.dispatch(fetchNewNumber());

        return store.getState().db.contact.newNumber;
    },
);

/******* Email Management *******/
interface SaveEmailType {
    personId: number;
    email: EmailAddress;
}
const fetchNewEmail = createAsyncThunk("contact/fetchNewEmail", async () => {
    const response = await Datastore.getType<EmailAddress>(
        "api/EmailAddresses/New",
    );
    return response;
});
export const saveNewEmail = createAsyncThunk(
    "contact/saveNewEmail",
    async (payload: SaveEmailType) => {
        const response = await Datastore.postType<EmailAddress[]>(
            `api/EmailAddresses/People/${payload.personId}`,
            [payload.email],
        );
        return response;
    },
);
export const saveEmail = createAsyncThunk(
    "contact/saveEmail",
    async (email: EmailAddress) => {
        const response = await Datastore.putType<EmailAddress[]>(
            "api/EmailAddresses",
            [email],
        );
        return response;
    },
);
export const deleteEmail = createAsyncThunk(
    "contact/deleteEmail",
    async (email: EmailAddress) => {
        const response = await Datastore.delete(
            `api/EmailAddresses/${email.id}`,
        );
        return response;
    },
);
export const getNewEmail = createAsyncThunk(
    "contact/getNewEmail",
    async (_, thunkAPI) => {
        const { contact } = store.getState().db;

        if (contact.newEmailStatus === "idle")
            await thunkAPI.dispatch(fetchNewEmail());

        return store.getState().db.contact.newEmail;
    },
);

/******* Address Management *******/
interface SaveAddressType {
    personId: number;
    address: Address;
}
const fetchNewAddress = createAsyncThunk(
    "contact/fetchNewAddress",
    async () => {
        const response = await Datastore.getType<Address>("api/Addresses/New");
        return response;
    },
);
export const saveNewAddress = createAsyncThunk(
    "contact/saveNewAddress",
    async (payload: SaveAddressType) => {
        const response = await Datastore.postType<Address[]>(
            `api/Addresses/People/${payload.personId}`,
            [payload.address],
        );
        return response;
    },
);
export const saveAddress = createAsyncThunk(
    "contact/saveAddress",
    async (address: Address) => {
        const response = await Datastore.putType<Address[]>("api/Addresses", [
            address,
        ]);
        return response;
    },
);
export const deleteAddress = createAsyncThunk(
    "contact/deleteAddress",
    async (address: Address) => {
        const response = await Datastore.delete(`api/Addresses/${address.id}`);
        return response;
    },
);
export const getNewAddress = createAsyncThunk(
    "contact/getNewAddress",
    async (_, thunkAPI) => {
        const { contact } = store.getState().db;

        if (contact.newAddressStatus === "idle")
            await thunkAPI.dispatch(fetchNewAddress());

        return store.getState().db.contact.newAddress;
    },
);

// We have to export the reducer as this is added to the state store
export default contactSlice.reducer;
